/**
 * Money handed over in person — a check or cash — and its receipt.
 *
 * Jonathon, 2026-09-10, after Sherley Gillespie's $450 check had to be
 * receipted by hand: "we need to build a process where this happens
 * automatically." Saving here records the payment, ticks what it ticks, and
 * emails the customer a receipt on the letterhead with the PDF attached; the
 * same PDF goes on the Docs card. Nobody is copied. See ADR 0125.
 *
 * Opened from the Balance card, and by ticking First or Final Check Submitted
 * by hand — "when a rep clicks on payment submitted".
 *
 * The screen chooses nothing about money itself. Which payment an amount is,
 * and what it ticks, is `packages/core`; the balance on the receipt is
 * `jobBalance`. The amount box starts at the payment chosen because that is
 * what the customer was asked for, and it stays editable because what they
 * handed over is what gets recorded.
 */

import {
  type Balance,
  type CustomerPaymentMethod,
  type Job,
  type PaymentSlot,
  CUSTOMER_PAYMENT_METHODS,
  CUSTOMER_PAYMENT_METHOD_LABEL,
  formatMoney,
  cents,
  mayPaySlot,
  toCents,
  toDecimal,
} from "@roof-hawk/core";
import { useRef, useState } from "react";

import { ErrorNote, Modal } from "../../components/ui";
import { t } from "../../i18n";
import {
  queueReceiptForReview,
  recordCustomerPayment,
  sendCustomerPaymentReceipt,
} from "../../lib/payments";
import { useSession } from "../../session";
import { ReceiptReviewModal } from "./ReceiptReviewModal";

/** Today, as an ISO date in the reader's own timezone. */
function today(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

/** The "Which payment" choice for a monthly payment toward the plan. */
const PLAN = "plan";

/** What goes in the amount box: dollars, as typed. */
const typed = (amount: number) => toDecimal(amount as never).toFixed(2);

export function RecordPaymentModal({
  job,
  balance,
  initialSlot,
  clearedByBoxSlot,
  onClose,
  onRecorded,
  plan = null,
}: {
  job: Job;
  balance: Balance | null;
  /**
   * The job's in-house payment plan (ADR 0286). With one, a monthly check is
   * recorded toward the plan at its own amount rather than as the Final.
   */
  plan?: { monthlyCents: number } | null;
  /**
   * The payment to start on. Given by the Submitted box that opened this;
   * otherwise the first payment still outstanding.
   */
  initialSlot?: PaymentSlot | null | undefined;
  /** The already-ticked cleared box that opened this offer (ADR 0182). */
  clearedByBoxSlot?: PaymentSlot | undefined;
  onClose: () => void;
  onRecorded: () => Promise<void>;
}) {
  const { profileId } = useSession();
  // On a plan the Final is paid month by month, never as one payment, so it
  // is not offered: recording a $100 check as the Final would settle all of it.
  // The rule is `mayPaySlot` in packages/core, which recording asks as well.
  const lines = (balance?.lines ?? []).filter((line) =>
    mayPaySlot(line.slot, Boolean(plan)),
  );
  const firstUnpaid = lines.find((line) => !line.paid)?.slot ?? null;
  const startSlot = initialSlot !== undefined ? initialSlot : firstUnpaid;
  const startLine = lines.find((line) => line.slot === startSlot) ?? null;
  // A plan job starts on the plan once the down payment is in, and when the
  // Final's own box opened this: on a plan, that box's money is a monthly one.
  const startOnPlan =
    Boolean(plan) &&
    !startLine &&
    (initialSlot === undefined ||
      (initialSlot !== null && !mayPaySlot(initialSlot, true)));

  const [method, setMethod] = useState<CustomerPaymentMethod>("check");
  const [slot, setSlot] = useState(
    startOnPlan ? PLAN : startSlot ? String(startSlot) : "",
  );
  const [amount, setAmount] = useState(
    startOnPlan && plan
      ? typed(plan.monthlyCents)
      : startLine
        ? typed(startLine.amount)
        : "",
  );
  const [checkNumber, setCheckNumber] = useState("");
  const [receivedOn, setReceivedOn] = useState(today());
  // Off for a payment somebody already receipted by hand, so the customer is
  // not sent a second one.
  const [emailIt, setEmailIt] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{
    number: string;
    sentTo: string | null;
    why: string | null;
  } | null>(null);
  /** The payment whose receipt is being read over before it goes (ADR 0140). */
  const [reviewing, setReviewing] = useState<string | null>(null);
  /**
   * The payment is on the job, but something after it — the stamp, the
   * receipt, the refresh — threw. Terminal: see `save`.
   */
  const [stranded, setStranded] = useState<{
    number: string;
    problem: string;
  } | null>(null);

  const email = (job.contact?.email ?? "").trim();

  const choose = (value: string) => {
    setSlot(value);
    if (value === PLAN && plan) {
      setAmount(typed(plan.monthlyCents));
      return;
    }
    const line = lines.find((row) => String(row.slot) === value);
    if (line) setAmount(typed(line.amount));
  };

  /*
   * One press, one payment.
   *
   * `busy` disables the button, but it is state: two clicks dispatched before
   * React has re-rendered both read it as false, and the second one records a
   * second check. Nothing downstream would catch it — a customer really can
   * hand over two checks for the same amount on the same day, so neither the
   * database nor the receipt numbering can tell an accident from a fact. The
   * job would carry twice the money, twice the receipt, and a balance that had
   * to be put right by hand (Josh, 2026-09-14). A ref is read and written in
   * the same tick, so the second press sees the first.
   */
  const saving = useRef(false);

  const save = async () => {
    if (saving.current) return;
    const amountCents = toCents(amount);
    if (!(amountCents > 0)) {
      setError(t("Type the amount you were given."));
      return;
    }
    saving.current = true;
    setBusy(true);
    setError(null);
    /*
     * Once this is set the money is on the job, and Save is never offered
     * again.
     *
     * The ref above stops two presses landing together, but it was let go in
     * `finally` — so when anything after the payment threw (the stamp, the
     * receipt, the refresh), the form came back with Save live and the same
     * figures in it, and the obvious next press recorded the check a second
     * time (Josh, review of #234, 2026-09-15). A failure after this point is
     * a receipt problem, not a payment problem, and it is shown as one: the
     * payment stands, and its receipt is sent from the Balance card.
     */
    let recorded: { receipt_number: string } | null = null;
    try {
      const { payment } = await recordCustomerPayment({
        jobId: job.id,
        method,
        amountCents,
        checkNumber: method === "check" ? checkNumber : null,
        receivedOn,
        slot: slot && slot !== PLAN ? (Number(slot) as PaymentSlot) : null,
        towardPlan: slot === PLAN,
        clearedByBoxSlot,
        profileId: profileId ?? null,
      });
      recorded = payment;
      if (emailIt && email) {
        /*
         * Read before it goes (ADR 0140).
         *
         * The receipt used to leave the moment this was saved. Jonathon,
         * 2026-09-12: "this email needs to be a prompt we can review before
         * sending it." The rep is standing there, so they get the prompt now
         * rather than finding it on their list later; the payment is already
         * recorded either way, and closing without sending leaves the receipt
         * waiting rather than losing it.
         *
         * That last part is this line. The receipt is stamped as waiting
         * before the prompt opens — the same stamp a cleared check gets — so
         * "Don't send", the X, or a closed tab all leave it on the rep's list,
         * and sending is what takes it off. Stamping only on "Don't send"
         * would miss every way out that is not that button, and cash never
         * comes back round through the bank to be stamped later.
         */
        await queueReceiptForReview(payment.id);
        await onRecorded();
        setReviewing(payment.id);
        return;
      }
      const result = await sendCustomerPaymentReceipt(
        payment.id,
        profileId ?? null,
        { email: false },
      );
      setDone({
        number: payment.receipt_number,
        sentTo: result.to,
        why: result.why,
      });
      await onRecorded();
    } catch (cause) {
      const problem = cause instanceof Error ? cause.message : String(cause);
      if (recorded) {
        setStranded({ number: recorded.receipt_number, problem });
        // The job behind this still has to show the payment that is on it.
        await onRecorded().catch(() => {});
        return;
      }
      setError(problem);
    } finally {
      // Let go only if nothing was recorded. After a payment, this form is
      // finished with whatever happens next.
      if (!recorded) {
        saving.current = false;
        setBusy(false);
      }
    }
  };

  // `done` says more exactly what happened, when the receipt got that far and
  // only the refresh after it threw.
  if (stranded && !done) {
    return (
      <Modal
        title={t("Payment recorded")}
        onClose={onClose}
        actions={
          <button type="button" className="btn-primary" onClick={onClose}>
            {t("Close")}
          </button>
        }
      >
        <p>
          {t(
            "The payment is recorded on the job as receipt {number}, but its receipt did not go: {problem}. Do not record the payment again — send the receipt from the Balance card.",
            { number: stranded.number, problem: stranded.problem },
          )}
        </p>
      </Modal>
    );
  }

  if (reviewing) {
    return (
      <ReceiptReviewModal
        paymentId={reviewing}
        onClose={onClose}
        onSent={onRecorded}
      />
    );
  }

  if (done) {
    return (
      <Modal
        title={t("Payment recorded")}
        onClose={onClose}
        actions={
          <button type="button" className="btn-primary" onClick={onClose}>
            {t("Close")}
          </button>
        }
      >
        <p>
          {done.sentTo
            ? t("Receipt {number} was emailed to {to} and saved to Docs.", {
                number: done.number,
                to: done.sentTo,
              })
            : t(
                "Receipt {number} is saved to Docs, but it was not emailed: {why}. Resend it from the Balance card.",
                {
                  number: done.number,
                  why: done.why ?? t("no reason was given"),
                },
              )}
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      title={t("Record a payment")}
      subtitle={t("The customer is emailed a receipt when you save.")}
      onClose={onClose}
      actions={
        <>
          <button
            type="button"
            className="btn-ghost"
            onClick={onClose}
            disabled={busy}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => void save()}
            disabled={busy}
          >
            {busy
              ? t("Saving…")
              : emailIt && email
                ? // The receipt is shown before it goes (ADR 0140), and the
                  // button says so rather than promising a send.
                  t("Save and read the receipt")
                : t("Save the payment")}
          </button>
        </>
      }
    >
      <ErrorNote error={error} />
      <div className="fgrid one">
        <div className="field">
          <label>{t("How was it paid?")}</label>
          <div className="pay-method">
            {/* Jonathon, 2026-10-14: a card taken outside Roof Board, Zelle or
                a bank transfer, and a financing payout, as well (ADR 0288). */}
            {CUSTOMER_PAYMENT_METHODS.map((option) => (
              <button
                key={option}
                type="button"
                className={method === option ? "btn-small on" : "btn-small"}
                aria-pressed={method === option}
                onClick={() => setMethod(option)}
              >
                {t(CUSTOMER_PAYMENT_METHOD_LABEL[option])}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <label htmlFor="pay-slot">{t("Which payment")}</label>
          <select
            id="pay-slot"
            value={slot}
            onChange={(event) => choose(event.target.value)}
          >
            {lines.map((line) => (
              <option key={line.slot} value={String(line.slot)}>
                {t(line.label)} — {line.amountFormatted}
                {line.paid ? ` ${t("(already in)")}` : ""}
              </option>
            ))}
            {plan && (
              <option value={PLAN}>
                {t("Payment plan — {amount} a month", {
                  amount: formatMoney(cents(plan.monthlyCents)),
                })}
              </option>
            )}
            <option value="">{t("None of these")}</option>
          </select>
        </div>

        <div className="field">
          <label htmlFor="pay-amount">{t("Amount")}</label>
          <input
            id="pay-amount"
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </div>

        {method === "check" && (
          <div className="field">
            <label htmlFor="pay-check">{t("Check number")}</label>
            <input
              id="pay-check"
              value={checkNumber}
              onChange={(event) => setCheckNumber(event.target.value)}
            />
          </div>
        )}

        <div className="field">
          <label htmlFor="pay-date">{t("Date received")}</label>
          <input
            id="pay-date"
            type="date"
            value={receivedOn}
            onChange={(event) => setReceivedOn(event.target.value)}
          />
        </div>

        {email && (
          <label className="check-row">
            <input
              type="checkbox"
              checked={emailIt}
              onChange={(event) => setEmailIt(event.target.checked)}
            />{" "}
            {t("Email the receipt to {email}", { email })}
          </label>
        )}
      </div>

      <div className="na-hint">
        {method === "cash"
          ? t("Cash counts as paid straight away.")
          : method === "check"
            ? t(
                "A check counts as submitted now, and as paid once it shows up in Mercury.",
              )
            : t(
                "It counts as on its way now, and as paid once somebody presses It’s in the bank on the Payments tab.",
              )}{" "}
        {!email
          ? t(
              "There is no email address on file for this customer, so the receipt will only be saved to Docs.",
            )
          : emailIt
            ? t(
                "You will be shown the receipt to read before it goes, and it is saved to Docs as well.",
              )
            : t(
                "The receipt will only be saved to Docs — untick this only if the customer already has one.",
              )}
      </div>
    </Modal>
  );
}
